import { makeStyles, tokens } from '@fluentui/react-components';

/**
 * 'Graphite' and 'Slate' of the DOMCURA manual, the latter also innoAS' primary
 * grey. Warmer than the Fluent neutrals and shared by every tenant, so the copy
 * stays constant while only the accent changes.
 */
const TEXT_PRIMARY = '#4A4A49';
const TEXT_SECONDARY = '#5F5E5E';

export const useStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingVerticalXXL,
        padding: tokens.spacingHorizontalXXL,
        height: '100%',
        minHeight: 0,
        // The contact card is considerably taller than the form card and does
        // not fit the add-in's viewport on smaller screens.
        overflowY: 'auto',
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: tokens.spacingVerticalL,
        width: '100%',
        maxWidth: '760px',
        padding: tokens.spacingHorizontalXXXL,
        borderRadius: tokens.borderRadiusXLarge,
        // The card stays neutral so that the tenant's brand colour can carry the
        // icons alone; a saturated surface would compete with the copy.
        backgroundColor: tokens.colorNeutralBackground1,
        color: TEXT_PRIMARY,
    },
    headline: {
        margin: 0,
    },
    lead: {
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
        color: TEXT_SECONDARY,
    },
    cta: {
        height: '48px',
        paddingLeft: tokens.spacingHorizontalXXXL,
        paddingRight: tokens.spacingHorizontalXXXL,
        fontSize: tokens.fontSizeBase400,
        // Pill shape, matching the call to action on the tenant's own form.
        borderRadius: '9999px',
        border: 'none',
        color: tokens.colorNeutralForegroundOnBrand,
        // The fill is a runtime tenant colour, so hover and pressed states are
        // derived from it rather than from theme tokens.
        ':hover': {
            filter: 'brightness(1.1)',
            color: tokens.colorNeutralForegroundOnBrand,
        },
        ':active': {
            filter: 'brightness(0.92)',
            color: tokens.colorNeutralForegroundOnBrand,
        },
    },
    contacts: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: tokens.spacingHorizontalM,
        width: '100%',
    },
    contact: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        color: TEXT_PRIMARY,
        textDecorationLine: 'none',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3Hover,
            color: TEXT_PRIMARY,
        },
    },
    contactIcon: {
        display: 'flex',
        flexShrink: 0,
        fontSize: '20px',
    },
    contactText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    contactLabel: {
        color: TEXT_SECONDARY,
    },
    contactValue: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    divider: {
        width: '100%',
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke2,
    },
    checklist: {
        width: '100%',
        margin: 0,
        padding: 0,
        listStyleType: 'none',
        // Flows into a second column only when there is room for one, which
        // keeps the card compact on wide screens without cramping the add-in's
        // narrow task pane.
        columnCount: 2,
        columnWidth: '320px',
        columnGap: tokens.spacingHorizontalXXL,
    },
    checklistItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
        paddingBottom: tokens.spacingVerticalS,
        breakInside: 'avoid',
    },
    checklistIcon: {
        display: 'flex',
        flexShrink: 0,
        // Nudged onto the cap height of the first line of the item.
        paddingTop: '2px',
        fontSize: '16px',
    },
    note: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
        width: '100%',
        padding: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        color: TEXT_SECONDARY,
    },
    noteIcon: {
        display: 'flex',
        flexShrink: 0,
        paddingTop: '1px',
        fontSize: '16px',
    },
    noteText: {
        color: 'inherit',
    },
});
